import { h, Fragment } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import { useSignal } from '@preact/signals'
import { RoomItem } from './RoomItem.jsx'
import { FloatPanel } from './FloatPanel.jsx'
import { NewDirectModal } from './NewDirectModal.jsx'
import { NewGroupModal } from './NewGroupModal.jsx'
import { chatApi } from '../chat-api.js'
import { t } from '../chat-i18n.js'
import { chat_root_path } from '../../../routes'
import { setWidgetSlot } from '../../../lib/widget_manager.js'

const DEBOUNCE_MS = 400

async function refreshRooms(store) {
  try {
    const res = await chatApi.rooms.list()
    if (res.ok) {
      const data = await res.json
      store.rooms.value = Array.isArray(data) ? data : (data.rooms ?? [])
      store.roomsLoadError.value = null
    } else {
      store.roomsLoadError.value = t('chat.rooms_load_error')
    }
  } catch {
    store.roomsLoadError.value = t('chat.rooms_load_error')
  }
}

function upsertRoom(store, room) {
  if (!room?.id) return
  const list = store.rooms.value.filter(r => r.id !== room.id)
  store.rooms.value = [room, ...list]
}

/**
 * DockApp — full Preact chrome for the floating chat dock.
 *
 * Owns toggle + unread badge, header, new DM/group actions, room list body,
 * footer fullscreen link, float panels, and room-create modals.
 *
 * Props:
 *   store, cable, personId, personName?, avatarUrl?, unreadCount?
 */
export function DockApp({
  store,
  cable,
  personId,
  personName = '',
  avatarUrl = '',
  unreadCount = 0,
}) {
  const dockOpen     = useSignal(false)
  const showDirect   = useSignal(false)
  const showGroup    = useSignal(false)
  // Seed badge from server SSR value until rooms list loads
  const seedUnread   = useSignal(Number(unreadCount) || 0)

  useEffect(() => {
    store.roomsLoading.value = true
    refreshRooms(store).finally(() => {
      store.roomsLoading.value = false
      seedUnread.value = 0
    })
    setWidgetSlot('chatDock', { open: false })
    return () => setWidgetSlot('chatDock', { open: false })
  }, []) // eslint-disable-line

  useEffect(() => {
    setWidgetSlot('chatDock', { open: dockOpen.value })
  }, [dockOpen.value]) // eslint-disable-line

  // Persist float panel state
  useEffect(() => {
    if (!personId) return
    const key = `tsh:chatDock:${personId}.panels`
    const panels = store.floatPanels.value
    try {
      if (panels.length === 0) {
        localStorage.removeItem(key)
      } else {
        localStorage.setItem(key, JSON.stringify(panels))
      }
    } catch { /* ignore */ }
  }, [store.floatPanels.value]) // eslint-disable-line

  const floatPanels = store.floatPanels.value
  const roomsLoaded = !store.roomsLoading.value
  const badgeCount = roomsLoaded
    ? (store.totalUnread.value || 0)
    : (seedUnread.value || store.totalUnread.value || 0)

  function closeDock() {
    dockOpen.value = false
  }

  function toggleDock() {
    dockOpen.value = !dockOpen.value
  }

  function openFloatPanel(roomId) {
    const already = store.floatPanels.value.some(p => p.roomId === roomId)
    if (!already) {
      store.floatPanels.value = [...store.floatPanels.value, { roomId, minimized: false }]
    } else {
      store.floatPanels.value = store.floatPanels.value.map(p =>
        p.roomId === roomId ? { ...p, minimized: false } : p
      )
    }
    closeDock()
  }

  function handleRoomClick(room) {
    openFloatPanel(room.id)
  }

  async function handleRoomCreated(room) {
    upsertRoom(store, room)
    await refreshRooms(store)
    if (room?.id) openFloatPanel(room.id)
  }

  const displayName = personName || store.personName || ''
  const initial = (displayName || '?').slice(0, 1).toUpperCase()

  return (
    <>
      <div
        class={`chat-dock ${dockOpen.value ? 'is-open' : ''}`}
        id="chat-dock"
      >
        <button
          class="chat-dock-toggle rounded-circle"
          type="button"
          title={t('chat.dock.open')}
          aria-expanded={dockOpen.value ? 'true' : 'false'}
          onClick={toggleDock}
        >
          <i class="bi bi-chat-dots-fill" />
          <span
            class={`chat-dock-badge ${badgeCount > 0 ? 'chat-dock-badge--pop' : 'd-none'}`}
            id="chat-dock-toggle-badge"
            data-count={badgeCount}
          >
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        </button>

        <div class={`chat-dock-window ${dockOpen.value ? 'chat-dock-window--open' : ''}`}>
          <div class="chat-dock-header">
            <div class="chat-dock-header-left">
              <div class="chat-dock-header-avatar">
                {avatarUrl
                  ? <img src={avatarUrl} width={32} height={32} alt={displayName} />
                  : initial}
              </div>
              <div class="chat-dock-header-title">
                <span class="chat-dock-header-name">{displayName}</span>
                <span class="chat-dock-header-subtitle">{t('chat.title')}</span>
              </div>
            </div>
            <div class="chat-dock-header-actions">
              <a href={chat_root_path()} class="chat-dock-header-btn" title={t('chat.fullscreen')}>
                <i class="bi bi-arrows-fullscreen" />
              </a>
              <button
                type="button"
                class="chat-dock-header-btn"
                title={t('chat.dock.close')}
                onClick={closeDock}
              >
                <i class="bi bi-x-lg" />
              </button>
            </div>
          </div>

          {store.canManageRooms && (
            <div class="chat-dock-actions-row">
              <button
                type="button"
                class="chat-dock-new-btn"
                onClick={() => { showDirect.value = true }}
              >
                <i class="bi bi-person-plus-fill" />
                <span>{t('chat.new_direct')}</span>
              </button>
              <button
                type="button"
                class="chat-dock-new-btn"
                onClick={() => { showGroup.value = true }}
              >
                <i class="bi bi-people-fill" />
                <span>{t('chat.new_group')}</span>
              </button>
            </div>
          )}

          <div class="chat-dock-body">
            <DockRoomList store={store} cable={cable} onRoomClick={handleRoomClick} />
          </div>

          <div class="chat-dock-footer">
            <a href={chat_root_path()} class="chat-dock-footer-link">
              <i class="bi bi-chat-square-text" />
              <span>{t('chat.fullscreen')}</span>
              <i class="bi bi-arrow-right ms-auto" />
            </a>
          </div>
        </div>
      </div>

      {/* Float panels — portal into #chat-float-container (see FloatPanel) */}
      {floatPanels.map((panel, idx) => (
        <FloatPanel
          key={panel.roomId}
          dockStore={store}
          panelRoomId={panel.roomId}
          personId={personId}
          personName={store.personName}
          tenantId={store.tenantId}
          panelIndex={idx}
          minimized={panel.minimized}
        />
      ))}

      <NewDirectModal
        open={showDirect.value}
        onClose={() => { showDirect.value = false }}
        onCreated={handleRoomCreated}
      />
      <NewGroupModal
        open={showGroup.value}
        onClose={() => { showGroup.value = false }}
        onCreated={handleRoomCreated}
      />
    </>
  )
}

function DockRoomList({ store, cable, onRoomClick }) {
  const searchText    = useSignal('')
  const searchResults = useSignal(null)
  const debounceTimer = useRef(null)

  const rooms        = store.rooms.value
  const floatPanels  = store.floatPanels.value
  const roomsLoading = store.roomsLoading.value
  const roomsLoadError = store.roomsLoadError.value

  useEffect(() => {
    const q = searchText.value.trim()
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    if (!q) {
      searchResults.value = null
      return
    }
    debounceTimer.current = setTimeout(() => {
      chatApi.search.rooms(q).then(async res => {
        if (res.ok) {
          const data = await res.json
          searchResults.value = Array.isArray(data) ? data : (data.rooms ?? [])
        }
      }).catch(() => { /* ignore */ })
    }, DEBOUNCE_MS)
  }, [searchText.value]) // eslint-disable-line

  const q = searchText.value.trim().toLowerCase()
  const displayRooms = searchResults.value != null
    ? searchResults.value
    : q
      ? rooms.filter(r => (r.name || r.display_name || '').toLowerCase().includes(q))
      : rooms

  return (
    <div class="chat-dock-inner">
      <div class="chat-sidebar-search chat-dock-search">
        <i class="bi bi-search chat-sidebar-search-icon" />
        <input
          type="search"
          class="chat-sidebar-search-input"
          placeholder={t('chat.room_search_placeholder')}
          value={searchText.value}
          onInput={e => { searchText.value = e.target.value }}
        />
        {searchText.value && (
          <button
            type="button"
            class="chat-sidebar-search-clear"
            onClick={() => { searchText.value = ''; searchResults.value = null }}
          >
            <i class="bi bi-x" />
          </button>
        )}
      </div>

      <div class="chat-dock-rooms-list">
        {roomsLoading && rooms.length === 0 ? (
          <div class="chat-rooms-loader">
            <span class="spinner-border spinner-border-sm text-secondary" role="status" />
          </div>
        ) : roomsLoadError && rooms.length === 0 ? (
          <div class="chat-dock-empty">
            <p class="mb-2 px-3 text-center small">{roomsLoadError}</p>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              onClick={() => {
                store.roomsLoading.value = true
                refreshRooms(store).finally(() => { store.roomsLoading.value = false })
              }}
            >
              {t('chat.rooms_load_retry')}
            </button>
          </div>
        ) : displayRooms.length === 0 && q ? (
          <div class="chat-sidebar-empty">{t('chat.search_no_results')}</div>
        ) : displayRooms.length === 0 && !roomsLoading ? (
          <div class="chat-dock-empty">
            <i class="bi bi-chat-square-dots chat-dock-empty-icon" />
            <p>{t('chat.dock.no_rooms_empty')}</p>
          </div>
        ) : (
          displayRooms.map(room => (
            <RoomItem
              key={room.id}
              room={room}
              store={store}
              cable={cable}
              active={floatPanels.some(p => p.roomId === room.id)}
              onClick={() => onRoomClick(room)}
            />
          ))
        )}
      </div>
    </div>
  )
}
